/**
 * Speedjunkies Global Product Entries & Multiplier Updater
 * Runs globally on every page to fetch active multiplier and keep entries and flashing badges up-to-date,
 * including correct handling of active product overrides.
 */
(function() {
  // Always store a default/fallback multiplier on window
  window.currentGiveawayMultiplier = window.currentGiveawayMultiplier || 10;
  
  // In-memory cache to prevent redundant API fetches (e.g. on rapid variant swaps or scrolls)
  const entriesCache = {};

  // Helper: Retrieve price in cents for a given label
  function getPriceCents(label) {
    const container = label.closest('[data-product-id]');
    let priceCents = 0;
    const priceCentsAttr = container?.getAttribute('data-price');
    if (priceCentsAttr) {
      priceCents = parseFloat(priceCentsAttr);
    } else {
      const priceDollarsAttr = label.getAttribute('data-price-dollars');
      if (priceDollarsAttr) {
        priceCents = Math.round(parseFloat(priceDollarsAttr) * 100);
      }
    }
    return priceCents;
  }

  // Helper: Retrieve numeric product ID
  function getProductId(label) {
    const container = label.closest('[data-product-id]');
    const productId = container ? container.getAttribute('data-product-id') : null;
    return productId ? productId.split('/').pop() : null;
  }

  // Helper: Apply the entries and badge text to elements
  function applyProductEntry(label, productData, multiplier, priceCents) {
    if (priceCents !== undefined && priceCents !== null) {
      const currentPriceCents = getPriceCents(label);
      if (currentPriceCents !== priceCents) {
        return; // Skip stale updates from race conditions
      }
    }

    label.innerHTML = `+${productData.entries.toLocaleString()} ENTRIES`;
    
    const container = label.closest('[data-product-id]');
    const badge = container?.querySelector('[data-multiplier-badge]') || label.parentElement?.querySelector('[data-multiplier-badge]');
    if (badge) {
      badge.textContent = productData.hasOverride ? productData.label : `${multiplier}X`;
      badge.classList.remove('hidden');
      badge.style.display = 'inline-flex';
    }
  }

  // Helper: Update cart entries
  function updateCartEntries(multiplier) {
    const cartEntriesBox = document.querySelector('[data-cart-total-dollars]');
    const cartEntriesLabel = document.querySelector('[data-cart-entries-label]');
    if (cartEntriesBox && cartEntriesLabel) {
      const cartTotalDollars = parseFloat(cartEntriesBox.getAttribute('data-cart-total-dollars'));
      if (!isNaN(cartTotalDollars)) {
        cartEntriesLabel.innerHTML = `+${Math.round(cartTotalDollars * multiplier).toLocaleString()} ENTRIES`;
      }
    }
  }

  // Primary Update function
  async function updateGlobalProductEntries(multiplier) {
    const labels = document.querySelectorAll('[data-product-entries-label]');
    if (labels.length === 0) return;

    const productQueries = [];
    const labelMapping = [];

    labels.forEach(label => {
      const cleanId = getProductId(label);
      const priceCents = getPriceCents(label);

      if (cleanId && priceCents >= 0) {
        const cacheKey = `${cleanId}:${priceCents}`;

        if (entriesCache[cacheKey]) {
          applyProductEntry(label, entriesCache[cacheKey], multiplier, priceCents);
        } else {
          productQueries.push(`${cleanId}:${priceCents}`);
          labelMapping.push({ label, cleanId, priceCents, cacheKey });
        }
      } else {
        const priceDollars = parseFloat(label.getAttribute('data-price-dollars'));
        if (!isNaN(priceDollars)) {
          const calculatedEntries = Math.round(priceDollars * multiplier);
          label.innerHTML = `+${calculatedEntries.toLocaleString()} ENTRIES`;
        }
      }
    });

    if (productQueries.length > 0) {
      try {
        const uniqueQueries = [...new Set(productQueries)];
        const productsQueryStr = uniqueQueries.join(',');
        const res = await fetch(`/apps/api/product-entries?products=${encodeURIComponent(productsQueryStr)}`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.success && data.products) {
            labelMapping.forEach(({ label, cleanId, priceCents, cacheKey }) => {
              const productData = data.products[cleanId];
              if (productData) {
                entriesCache[cacheKey] = productData;
                applyProductEntry(label, productData, multiplier, priceCents);
              } else {
                const fallbackData = {
                  entries: Math.round((priceCents / 100.0) * multiplier),
                  label: `${multiplier}X`,
                  hasOverride: false
                };
                entriesCache[cacheKey] = fallbackData;
                applyProductEntry(label, fallbackData, multiplier, priceCents);
              }
            });
            updateCartEntries(multiplier);
            return;
          }
        }
      } catch (err) {
        console.error('[ProductEntriesUpdater] Failed to fetch product overrides:', err);
      }

      // Fallback for API failure
      labelMapping.forEach(({ label, priceCents }) => {
        const fallbackData = {
          entries: Math.round((priceCents / 100.0) * multiplier),
          label: `${multiplier}X`,
          hasOverride: false
        };
        applyProductEntry(label, fallbackData, multiplier, priceCents);
      });
    }

    updateCartEntries(multiplier);
  }

  // Expose globally so giveaway console or other components can leverage it
  window.updateGlobalProductEntries = updateGlobalProductEntries;

  // Global listener for variant changes
  document.addEventListener('variant:update', () => {
    setTimeout(() => {
      if (window.currentGiveawayMultiplier) {
        updateGlobalProductEntries(window.currentGiveawayMultiplier);
      }
    }, 50); // Minor tick to ensure DOM is swapped by theme price components
  });

  // Global listener for cart updates to keep cart entries synchronized
  document.addEventListener('cart:update', () => {
    setTimeout(() => {
      if (window.currentGiveawayMultiplier) {
        updateGlobalProductEntries(window.currentGiveawayMultiplier);
      }
    }, 100); // 100ms tick to let AJAX section rendering swap elements
  });

  // Global listener for discount updates
  document.addEventListener('discount:update', () => {
    setTimeout(() => {
      if (window.currentGiveawayMultiplier) {
        updateGlobalProductEntries(window.currentGiveawayMultiplier);
      }
    }, 100);
  });

  // Run initial update on load
  document.addEventListener('DOMContentLoaded', () => {
    updateGlobalProductEntries(window.currentGiveawayMultiplier);
  });
  
  // Run immediately as well
  updateGlobalProductEntries(window.currentGiveawayMultiplier);

  // Fetch current entry multiplier from API
  async function fetchMultiplier() {
    try {
      const multRes = await fetch('/apps/api/current-multiplier');
      if (multRes.ok) {
        const multData = await multRes.json();
        if (multData && multData.success) {
          const multiplier = multData.totalMultiplier || multData.baseMultiplier || 10;
          window.currentGiveawayMultiplier = multiplier;
          updateGlobalProductEntries(multiplier);
        }
      }
    } catch (err) {
      console.error('[ProductEntriesUpdater] Failed to fetch current multiplier:', err);
    }
  }

  fetchMultiplier();
})();
